/**
 * Magyar formátum-validátorok (Partner P1).
 *
 * - adószám: `xxxxxxxx-y-zz` (8-1-2), a 8. számjegy ellenőrző (súlyok 9,7,3,1,9,7,3).
 * - telefon: E.164 normalizálás `libphonenumber-js`-szel (HU region, `06` trunk strip).
 * - irányítószám: 4-számjegyű formátum (a város NEM derivált — a kód nem 1:1 településre,
 *   pl. 8984 = Petrikeresztúr/Gombosszeg/Iborfia → a város kézi marad, nincs hard-lock).
 *
 * Minden validátor üres inputra `{ valid: true }`-t ad (a kötelezőséget a form külön kezeli),
 * hogy az opcionális mezők ne dobjanak hibát üresen.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface ValidationResult {
  valid: boolean;
  /** i18n-kulcs a hibához (a hívó `t()`-vel oldja fel), ha invalid. */
  errorKey?: string;
  /** Normalizált érték, ha a validátor ad ilyet (pl. telefon E.164). */
  normalized?: string;
}

const VALID = { valid: true } as const;

/**
 * Magyar adószám ellenőrzés. Elfogadja kötőjellel vagy anélkül; csak a 8 jegyű
 * törzsszám + ellenőrző-számjegyet validálja (a 11-jegyű teljes alakot is).
 */
export function validateHuTaxNumber(input: string): ValidationResult {
  const raw = input.trim();
  if (!raw) return VALID;
  const digits = raw.replace(/[\s-]/g, '');
  // 8 (törzsszám) vagy 11 (teljes) számjegy elfogadott.
  if (!/^\d{8}(\d{3})?$/.test(digits)) {
    return { valid: false, errorKey: 'partner.validation.taxFormat' };
  }
  const base = digits.slice(0, 8);
  const weights = [9, 7, 3, 1, 9, 7, 3];
  const sum = weights.reduce((acc, w, i) => acc + w * Number(base[i]), 0);
  const mod = sum % 10;
  const checkDigit = mod === 0 ? 0 : 10 - mod;
  if (checkDigit !== Number(base[7])) {
    return { valid: false, errorKey: 'partner.validation.taxChecksum' };
  }
  return VALID;
}

/**
 * Magyar telefonszám → E.164 normalizálás. A `06`-trunk-prefixet a libphonenumber
 * HU-region kezeli. Sikertelen parse → invalid.
 */
export function validateHuPhone(input: string): ValidationResult {
  const raw = input.trim();
  if (!raw) return VALID;
  const parsed = parsePhoneNumberFromString(raw, 'HU');
  if (!parsed || !parsed.isValid()) {
    return { valid: false, errorKey: 'partner.validation.phoneFormat' };
  }
  return { valid: true, normalized: parsed.number };
}

/** Magyar irányítószám: pontosan 4 számjegy (a város nem derivált — lásd modul-komment). */
export function validateHuPostalCode(input: string): ValidationResult {
  const raw = input.trim();
  if (!raw) return VALID;
  if (!/^\d{4}$/.test(raw)) {
    return { valid: false, errorKey: 'partner.validation.postalFormat' };
  }
  return VALID;
}
